import { useCurrentUser } from '@qualio/ui-components';
import React from 'react';
import { useFlags } from '../external/LaunchDarklyAdapter';
import { QualityEvent } from '../types';

export type EventPermissionsContextType = {
  canModifyEvent: boolean;
  canRevertStep: boolean;
  canChangeOwner: boolean;
  canCloseEventEarly: boolean;
  canReopenEvent: boolean;
  isEventOwner: boolean;
  isEventCollaborator: boolean;
  canCreateAndModifyEvent: boolean;
  canAddDocumentToStep: boolean;
  canCreateTask: boolean;
  canReopenTask: boolean;
  canAddFormToStep: boolean;
  canCompleteTaskStep: boolean;
  canCompleteAnyTask: boolean;
  canOpenImportedEvent: boolean;
  isQuality: boolean;
  isEventOpen: boolean;
  canWorkOnEvent: boolean;
  canChangeDueDate: boolean;
  canManageCollaborators: boolean;
  isEventCollaboratorsEnabled: boolean;
};

export const EventPermissionsContext = React.createContext<EventPermissionsContextType | undefined>(undefined);

export type EventPermissionsProviderType = {
  userId: number;
  event: QualityEvent;
  children?: React.ReactNode;
};

export const EventPermissionsProvider: React.FC<EventPermissionsProviderType> = ({ userId, event, children }) => {
  const { permissions, userGroups } = useCurrentUser();
  const isEventCollaboratorsEnabled = useFlags('eventCollaborators');
  const role = userGroups[0];

  const isQuality = role === 'quality';
  const isEventOwner = event.owner_id === userId;
  const isEventCollaborator = event.collaborators.some((collaborator) => collaborator.id === userId);
  const isEventOpen = event.status === 'open';
  const isEventImportedOrOpen = event.status === 'imported' || event.status === 'open';

  const canCreateEvent = Boolean(permissions['can_create_issue']);
  const canWorkOnEvent = Boolean(permissions['can_work_on_issue']);

  const canModifyEvent = isEventOwner && canWorkOnEvent;
  const canCreateAndModifyEvent = canModifyEvent && canCreateEvent;

  const canRevertStep = isEventCollaboratorsEnabled
    ? canWorkOnEvent && (isEventOwner || isEventCollaborator) && isEventOpen
    : canModifyEvent && isEventOpen;
  const canChangeOwner = canWorkOnEvent && (isQuality || isEventOwner);
  const canCloseEventEarly = canModifyEvent && isQuality;
  const canReopenEvent = canWorkOnEvent && canCreateEvent && isQuality;
  const canAddDocumentToStep = isEventCollaboratorsEnabled
    ? canWorkOnEvent && (isEventOwner || isEventCollaborator)
    : canModifyEvent;
  const canCreateTask = isEventCollaboratorsEnabled
    ? canWorkOnEvent && (isEventOwner || isEventCollaborator) && isEventOpen
    : canCreateAndModifyEvent && isEventOpen;
  const canReopenTask = isEventCollaboratorsEnabled
    ? canWorkOnEvent && (isEventOwner || isEventCollaborator || isQuality) && isEventOpen
    : canWorkOnEvent && (isEventOwner || isQuality);
  const canAddFormToStep = isEventCollaboratorsEnabled
    ? canWorkOnEvent && (isEventOwner || isEventCollaborator) && isEventOpen
    : canCreateAndModifyEvent && isEventOpen;
  const canCompleteTaskStep = isEventCollaboratorsEnabled
    ? canWorkOnEvent && (isEventOwner || isEventCollaborator) && isEventOpen
    : canModifyEvent;
  const canCompleteAnyTask =
    canWorkOnEvent && (isEventOwner || (isEventCollaboratorsEnabled && isEventCollaborator)) && isEventOpen;
  const canOpenImportedEvent = canCreateAndModifyEvent;
  const canChangeDueDate = canWorkOnEvent && (isQuality || isEventOwner) && isEventImportedOrOpen;
  const canManageCollaborators = canWorkOnEvent && isEventOpen && (isEventOwner || isQuality || isEventCollaborator);

  const value = {
    canModifyEvent,
    canRevertStep,
    canChangeOwner,
    canCloseEventEarly,
    canReopenEvent,
    isEventOwner,
    isEventCollaborator,
    canCreateAndModifyEvent,
    canAddDocumentToStep,
    canCreateTask,
    canReopenTask,
    canAddFormToStep,
    canCompleteTaskStep,
    canCompleteAnyTask,
    canOpenImportedEvent,
    isQuality,
    isEventOpen,
    canWorkOnEvent,
    canChangeDueDate,
    canManageCollaborators,
    isEventCollaboratorsEnabled,
  };
  return <EventPermissionsContext.Provider value={value}>{children}</EventPermissionsContext.Provider>;
};
